// Alterações feitas no aparelho que ainda não chegaram ao servidor: as que
// esperam sinal e as que o servidor não aceitou (com o motivo e o que fazer).
import { h, trocar } from '../lib/dom.js';
import { data as formatarData, dinheiro, placa as formatarPlaca } from '../lib/formato.js';
import * as armazem from '../local/armazem.js';
import { puxarAgora, sincronizar } from '../local/sincronia.js';
import { nestaTela } from '../rotas.js';
import { carregarVinculos, ehMotorista, sessao } from '../sessao.js';
import { casca } from '../ui/casca.js';
import { avisar, carregando, confirmar, emblema, faixa, secao, vazio } from '../ui/componentes.js';
import { mensagemDeErro } from '../lib/erros.js';

// Onde abrir cada registro para corrigir (`linha`: como está no aparelho).
const LINKS = {
  veiculos: (id) => `#/veiculos/${id}`,
  colaboradores: (id) => `#/colaboradores/${id}`,
  regras_remuneracao: (id) => `#/regras/${id}`,
  clientes: (id) => `#/clientes/${id}`,
  empresas: () => '#/conta',
  viagens: (id) => `#/viagens/${id}`,
  fretes: (id) => `#/cargas/${id}`,
  lancamentos: (id, linha) => {
    if (linha?.tipo === 'despesa') return `#/despesas/${id}`;
    if (linha?.tipo === 'credito' || linha?.tipo === 'desconto') return `#/acertos/novo?colaborador=${linha.colaborador_id}`;
    return `#/vales/${id}`;
  },
  acertos: (id) => `#/acertos/${id}`,
  parcelas: (_id, linha) => (linha?.lancamento_id ? `#/despesas/${linha.lancamento_id}` : '#/apagar'),
  anexos: (_id, linha) => (linha?.frete_id ? `#/cargas/${linha.frete_id}` : linha?.lancamento_id ? `#/despesas/${linha.lancamento_id}` : '#/despesas'),
  contas_fixas: (id) => `#/contas-fixas/${id}`,
};

// No app do motorista, as telas dele.
const LINKS_DO_MOTORISTA = {
  fretes: (id) => `#/m/cargas/${id}`,
  lancamentos: (id) => `#/m/despesas/${id}`,
  anexos: (_id, linha) => (linha?.lancamento_id ? `#/m/despesas/${linha.lancamento_id}` : '#/m/despesas'),
  oficinas: () => '#/sos',
};

async function linhaDoItem(item) {
  if (item.tabela === 'empresas') return sessao.empresa;
  return (await armazem.uma(item.tabela, item.registro_id)) ?? item.dados;
}

async function nomeDoRegistro(item) {
  const linha = await linhaDoItem(item);
  switch (item.tabela) {
    case 'veiculos':
      return `${linha?.tipo === 'implemento' ? 'Carreta' : 'Caminhão'} ${formatarPlaca(linha?.placa ?? '')}`.trim();
    case 'colaboradores':
      return linha?.apelido || linha?.nome || 'Colaborador';
    case 'clientes':
      return `Cliente ${linha?.nome ?? ''}`.trim();
    case 'regras_remuneracao': {
      const pessoa = linha?.colaborador_id ? await armazem.uma('colaboradores', linha.colaborador_id) : null;
      return `Remuneração${pessoa ? ` de ${pessoa.apelido || pessoa.nome}` : ''}`;
    }
    case 'empresas':
      return 'Dados da empresa';
    case 'viagens':
      return `Viagem de ${formatarData(linha?.data_saida)}`;
    case 'fretes':
      return `Carga de ${dinheiro(linha?.valor_frete)} (${formatarData(linha?.data_carregamento)})`;
    case 'lancamentos': {
      if (linha?.tipo === 'despesa') {
        const cat = linha.categoria_id ? await armazem.uma('categorias', linha.categoria_id) : null;
        return `${cat?.exige_hodometro ? 'Abastecimento' : `Despesa${cat ? ` (${cat.nome})` : ''}`} de ${dinheiro(linha.valor)} (${formatarData(linha.data)})`;
      }
      const nomes = { vale: 'Vale', credito: 'Crédito', desconto: 'Desconto', pagamento: 'Pagamento', devolucao: 'Devolução' };
      return `${nomes[linha?.tipo] ?? 'Lançamento'} de ${dinheiro(linha?.valor)} (${formatarData(linha?.data)})`;
    }
    case 'acertos':
      return `Assinatura do acerto nº ${linha?.numero ?? ''}/${linha?.ano ?? ''}`;
    case 'parcelas':
      return `Parcela ${linha?.numero ?? ''} de ${dinheiro(linha?.valor)} (vence ${formatarData(linha?.vencimento)})`;
    case 'anexos': {
      const dono = linha?.lancamento_id ? await armazem.uma('lancamentos', linha.lancamento_id) : null;
      return `${linha?.tipo === 'pdf' ? 'PDF' : 'Foto'} do comprovante${dono ? ` da despesa de ${dinheiro(dono.valor)}` : ''}`;
    }
    case 'contas_fixas':
      return `Conta fixa ${linha?.descricao ?? ''}`.trim();
    default:
      return item.tabela;
  }
}

function acaoDoItem(item) {
  if (item.tipo === 'criar') return 'Cadastro novo';
  if (item.dados?.deleted_at) return 'Exclusão';
  return 'Alteração';
}

export function telaPendencias() {
  const tela = nestaTela();
  const corpo = h('div', { class: 'secao' }, carregando());

  const tentar = async (botao) => {
    botao.disabled = true;
    try {
      await armazem.tentarDeNovo();
      const { online } = await sincronizar({ puxar: true });
      if (!online) avisar('Sem sinal agora. As alterações continuam guardadas e vão quando o sinal voltar.', 'erro');
    } catch (erro) {
      avisar(mensagemDeErro(erro), 'erro');
    }
    tela.renderizar();
  };

  const descartar = async (item, nome) => {
    const ok = await confirmar({
      titulo: 'Descartar esta alteração?',
      texto:
        item.tipo === 'criar'
          ? `${nome} foi cadastrado neste aparelho e ainda não chegou ao servidor. Se descartar, o cadastro some.`
          : `As mudanças em ${nome} feitas neste aparelho se perdem e fica valendo o que está no servidor.`,
      sim: 'Descartar',
      perigo: true,
    });
    if (!ok) return;
    try {
      const sumiu = await armazem.descartarRegistro(item.tabela, item.registro_id);
      if (!sumiu && sessao.empresa) {
        // Traz de volta a versão do servidor (se houver sinal; sem sinal, ela
        // volta na próxima baixa).
        const volta = item.tabela === 'empresas' ? carregarVinculos() : puxarAgora(sessao.empresa.id, [item.tabela]);
        await volta.catch(() => {});
      }
      avisar('Alteração descartada.');
    } catch (erro) {
      avisar(mensagemDeErro(erro), 'erro');
    }
    tela.renderizar();
  };

  (async () => {
    const itens = await armazem.filaCompleta();
    if (itens.length === 0) {
      trocar(corpo, vazio({ simbolo: 'check', titulo: 'Tudo enviado', texto: 'Não há alterações esperando envio neste aparelho.' }));
      return;
    }
    const recusados = itens.filter((i) => i.estado === 'recusada');
    const espera = armazem.esperas(itens);
    const cartoes = [];
    for (const item of itens) {
      const nome = await nomeDoRegistro(item);
      const links = ehMotorista() ? LINKS_DO_MOTORISTA : LINKS;
      const abrir = links[item.tabela]?.(item.registro_id, await linhaDoItem(item)) ?? (ehMotorista() ? '#/m' : '#/inicio');
      const recusado = item.estado === 'recusada';
      // O que segura um item que está esperando (outro registro recusado, ou
      // que ainda não chegou ao servidor).
      const aguarda = recusado ? null : espera.get(item.seq);
      let depoisDe = null;
      if (aguarda?.motivo === 'referencia') {
        const outro = itens.find((i) => i.tabela === aguarda.tabela && i.registro_id === aguarda.registro_id);
        depoisDe = `Vai depois de: ${outro ? await nomeDoRegistro(outro) : 'outro cadastro'}.`;
      } else if (aguarda) {
        depoisDe = 'Vai depois da alteração anterior deste mesmo cadastro.';
      }
      cartoes.push(h('div', { class: `cartao pendencia${recusado ? ' recusada' : ''}` },
        h('div', { class: 'cartao-corpo' },
          h('span', { class: 'cartao-titulo' }, nome),
          h('span', { class: 'cartao-linha' }, acaoDoItem(item)),
          recusado && h('span', { class: 'cartao-linha motivo' }, item.erro?.mensagem ?? 'O servidor não aceitou.'),
          depoisDe && h('span', { class: 'cartao-linha' }, depoisDe),
          h('span', { class: 'cartao-emblemas' }, recusado ? emblema('Não aceito', 'perigo') : emblema('Esperando envio', 'alerta')),
          recusado && h('div', { class: 'acoes-lado' },
            h('a', { class: 'botao secundario', href: abrir }, 'Abrir para corrigir'),
            h('button', { class: 'botao texto', type: 'button', onClick: () => descartar(item, nome) }, 'Descartar')))));
    }
    const botaoTentar = h('button', { class: 'botao cheio', type: 'button', onClick: () => tentar(botaoTentar) }, 'Tentar enviar de novo');
    trocar(corpo,
      recusados.length > 0
        ? faixa('O servidor não aceitou estas alterações. Abra o item para corrigir (ao salvar, ele é enviado de novo) ou descarte a alteração.', 'perigo')
        : faixa('Estas alterações estão guardadas neste aparelho e vão para o servidor assim que houver sinal.', 'info'),
      secao('Neste aparelho', h('div', { class: 'lista' }, cartoes), { contador: String(itens.length) }),
      h('div', { class: 'acoes' }, botaoTentar));
  })().catch((erro) => {
    console.error(erro);
    trocar(corpo, faixa(mensagemDeErro(erro), 'perigo'));
  });

  return casca({ titulo: 'Alterações não enviadas', voltar: ehMotorista() ? '/m' : '/inicio', conteudo: corpo });
}
